import { useCallback, useEffect, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Alert, Box, IconButton, Link, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  defaultStatus,
  findStatus,
  getStatusColumnObject,
  handleDoubleClickEvent,
  prepareSpendMaps,
  processRowUpdateForStatusOnly,
  SPEND_COLUMNS
} from './SupplierDataGridUtils';
import {
  NON_STRATEGIC,
  SRO_SBM_PROP_NAME,
  SRO_SBM_USER_ID_PROP_NAME,
  STRATEGIC,
  SUPPLIER_STATUS_CONTROL,
  SUPPLIER_STATUS_GROW,
  SUPPLIER_STATUS_MAINTAIN
} from '../../shared/AppConstants';
import { supplierDataGridSx } from './supplierDataGridStyles';
import MultilineCellEdit from './MultilineCellEdit';
import SupplierStatusPopup from './SupplierStatusPopup';
import {
  getAERatingColumn,
  sbmColumn
} from './prospective_supplier/PSSupplierDataGridUtils';
import { getEnvVar } from '../../utils/getEnvVar';

export function NonStrategicSupplierDataGrid(props) {
  const {
    apiRef,
    isEdit,
    unitGroups,
    strategicSuppliers,
    updateNonStrategicSupplierComments,
    updateNonStrategicSupplierStatus,
    dispatchSupplier,
    dispatchNonStrategicSupplier,
    setNonStrategicSuppliers,
    setNonSupplier,
    nonSupplier
  } = props;

  const riskRatingOnlineAppUrl = getEnvVar(
    'VITE_SUPPLIERS_RISK_RATING_ONLINE_APP_URL'
  );

  const [showSupplierCommentsEditModal, setShowSupplierCommentsEditModal] =
    useState(false);
  const handleCommentsModalCancel = () =>
    setShowSupplierCommentsEditModal(false);
  const [originalSupplier, setOriginalSupplier] = useState(null);
  const [updatedSupplier, setUpdatedSupplier] = useState(null);
  const [resetStatus, setResetStatus] = useState(null);

  const columns = [
    {
      field: 'number',
      headerName: 'Number',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => {
        const isBlockedSupplier = params.row.activeSupplier === 'N';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{params.value}</span>
            {isBlockedSupplier && (
              <Tooltip title="Blocked">
                <Box
                  component="span"
                  sx={(theme) => ({
                    color: theme.fuel6Tokens.JddcColorIconError,
                    marginLeft: '0.25em',
                    display: 'inline-flex'
                  })}
                >
                  <fuel-icon name="do_not_disturb_on" />
                </Box>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 320,
      renderCell: (params) => params.value
    },
    getStatusColumnObject(isEdit, NON_STRATEGIC),
    ...SPEND_COLUMNS,
    {
      field: 'forecastSpendPercentage',
      align: 'right',
      headerName: 'Forecast %',
      flex: 1,
      minWidth: 110,
      renderCell: (params) =>
        (params.row.forecastSpendPercentage
          ? Math.round(params.row.forecastSpendPercentage)
          : '0') + '%'
    },
    {
      field: 'riskRating',
      headerName: 'Risk Rating',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => (
        <Link
          href={`${riskRatingOnlineAppUrl}${params.row.number}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'sdis',
      headerName: 'SD Indicators',
      flex: 1,
      minWidth: 130,
      sortable: false,
      valueFormatter: (value) => (value ? value.join(', ') : value)
    },
    getAERatingColumn(),
    sbmColumn,
    {
      field: 'comments',
      headerName: 'Comments',
      flex: 2,
      minWidth: 220,
      editable: isEdit,
      resizable: true,
      renderEditCell: (params) => <MultilineCellEdit {...params} />,
      renderCell: (params) => (
        <Tooltip title={params?.value?.replaceAll(/(<([^</>]+)>)/gi, ' ')}>
          <div
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%'
            }}
          >
            {isEdit && (
              <IconButton size="small">
                <fuel-icon name="edit" />
              </IconButton>
            )}
            {params.value}
          </div>
        </Tooltip>
      )
    }
  ];

  function prepareSupplier(dataRows, splr, unitGroups, masterSpendObj) {
    if (splr && unitGroups) {
      const unitGroupsSpend = [];
      const splrStatus = findStatus(splr);

      let supplier = {
        id: dataRows ? dataRows.length : nextRowId,
        number: splr.supplierNumber,
        name: splr.supplierName,
        status: splrStatus,
        r12Spend: splr.invoiceSpend,
        r12Forecast: splr.forecastSpend,
        forecastSpendPercentage: splr.forecastSpendPercentage,
        comments: splr?.comments?.replaceAll(/(<([^</>]+)>)/gi, ' '),
        unitGroupsSpend: unitGroupsSpend,
        statusMapSize: splr?.statusMap?.size,
        riskRating: splr.riskRating,
        sroSbmName: splr[SRO_SBM_PROP_NAME],
        sroSbmUserId: splr[SRO_SBM_USER_ID_PROP_NAME],
        sdis: splr.sdis,
        aeRating: splr.aeRating,
        activeSupplier: splr?.activeSupplier
      };

      dataRows?.push(supplier);
      return supplier;
    }
  }

  function prepareData(strategicSuppliers, unitGroups) {
    const dataRows = [];
    const masterSpendObj = prepareSpendMaps(strategicSuppliers);

    strategicSuppliers?.forEach((splr) =>
      prepareSupplier(dataRows, splr, unitGroups, masterSpendObj)
    );
    setRows(dataRows);
    setNextRowId(dataRows.length);
  }

  const [rows, setRows] = useState([]);
  const [nextRowId, setNextRowId] = useState();
  const [addedRowId, setAddedRowId] = useState(-1);

  useEffect(() => {
    if (strategicSuppliers && unitGroups) {
      prepareData(strategicSuppliers, unitGroups);
    }
  }, [strategicSuppliers, unitGroups]);

  const handleCellClick = useCallback(
    (params, event) => handleDoubleClickEvent(params, event),
    []
  );

  function processRowUpdate(
    updatedRow,
    originalRow,
    updateSupplierStatus,
    updateSupplierComments
  ) {
    let processStateChange = true;

    if (
      updatedRow.status === SUPPLIER_STATUS_GROW ||
      updatedRow.status === SUPPLIER_STATUS_MAINTAIN ||
      updatedRow.status === SUPPLIER_STATUS_CONTROL
    ) {
      setShowSupplierCommentsEditModal(true);
      processStateChange = false;
    }

    if (updatedRow.status === defaultStatus) {
      apiRef.current.startCellEditMode({ id: 0, field: 'status' });
    }

    if (updatedRow.comments !== originalRow.comments) {
      if (processStateChange) {
        updateSupplierComments(updatedRow.number, updatedRow.comments);
      }

      if (addedRowId !== -1) {
        setAddedRowId(-1);
      }
    } else if (updatedRow.status !== originalRow.status) {
      setOriginalSupplier(originalRow);
      setUpdatedSupplier(updatedRow);
      if (processStateChange)
        processRowUpdateForStatusOnly(updatedRow, updateSupplierStatus);
    }

    return updatedRow;
  }

  useEffect(() => {
    if (resetStatus)
      apiRef.current.startCellEditMode({ id: addedRowId, field: 'status' });
  }, [resetStatus]);

  const NoRowsMsg = () => (
    <Box>
      <Alert
        variant="info"
        sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingSmall })}
      >
        No Strategic Suppliers have been added to this strategy
      </Alert>
    </Box>
  );

  return (
    <>
      <Box sx={{ mt: 4 }} width="100%">
        <DataGridPro
          autoHeight={true}
          apiRef={apiRef}
          density="compact"
          initialState={{
            sorting: {
              sortModel: [{ field: 'r12Forecast', sort: 'desc' }]
            },
            pinnedColumns: { right: ['actions'] }
          }}
          processRowUpdate={(updatedRow, originalRow) => {
            return processRowUpdate(
              updatedRow,
              originalRow,
              updateNonStrategicSupplierStatus,
              updateNonStrategicSupplierComments
            );
          }}
          slots={{
            noRowsOverlay: NoRowsMsg
          }}
          getRowHeight={() => 'auto'}
          columns={columns}
          rows={rows}
          onCellClick={handleCellClick}
          sx={supplierDataGridSx}
        />
      </Box>

      {isEdit &&
        originalSupplier &&
        updatedSupplier &&
        showSupplierCommentsEditModal && (
          <SupplierStatusPopup
            showModal={showSupplierCommentsEditModal}
            handleCancel={handleCommentsModalCancel}
            supplier={originalSupplier}
            updatedSupplier={updatedSupplier}
            setShowModal={setShowSupplierCommentsEditModal}
            updateSupplierStatus={updateNonStrategicSupplierStatus}
            setResetStatus={setResetStatus}
            dispatchNonStrategicSupplier={dispatchNonStrategicSupplier}
            dispatchSupplier={dispatchSupplier}
            setNonStrategicSuppliers={setNonStrategicSuppliers}
            setNonSupplier={setNonSupplier}
            nonSupplier={nonSupplier}
            movingToStrategicOrNonStrategic={STRATEGIC}
          />
        )}
    </>
  );
}
